import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';
import truncate from 'lodash/truncate';

import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

import { Button } from 'uikit/buttons';
import { Menu, MenuItem } from 'uikit/menus';
import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';

import Breadcrumb from './breadcrumb';
import { useScreenSize } from '../../utils';

import styles from '../styles/uikitLayoutsBreadcrumbs.module.scss';

const Breadcrumbs = ({
  breadcrumbs = [],
  className = '',
  shouldInvertBreadcrumbs = false,
  shouldShowAllAsActive = false,
}) => {
  const screenSize = useScreenSize();
  const isNarrow = screenSize === 'xsmall' || screenSize === 'small';

  const maxChars = isNarrow ? 15 : 45;

  const freshBreadcrumbs = [...breadcrumbs];
  const totalCrumbsCount = freshBreadcrumbs.length;

  if (totalCrumbsCount === 0) return null;

  const shouldShowKebabMenu = isNarrow ? totalCrumbsCount > 3 : totalCrumbsCount > 4;

  const firstCrumb = freshBreadcrumbs.shift();
  const lastCrumb = freshBreadcrumbs.pop();
  const secondCrumb = shouldShowKebabMenu ? null : freshBreadcrumbs.shift();
  const penultimateCrumb = isNarrow && shouldShowKebabMenu ? null : freshBreadcrumbs.pop();

  const getBreadcrumb = (breadcrumb) => (
    <Breadcrumb
      key={breadcrumb?.key}
      breadcrumb={breadcrumb}
      isCurrentLocation={breadcrumb === lastCrumb}
      maxChars={maxChars}
      shouldShowAllAsActive={shouldShowAllAsActive}
      shouldShowDivider={breadcrumb !== lastCrumb}
    />
  );

  const getMenuText = (text) => (
    <Typo color="inherit" variant="s02">
      {text}
    </Typo>
  );

  return (
    <nav
      className={classnames(styles.element, className, {
        [styles.isInverted]: shouldInvertBreadcrumbs,
      })}
      aria-label={i18n.t('breadcrumbs', 'Breadcrumbs')}
    >
      <ol className={styles.list}>
        {getBreadcrumb(firstCrumb)}
        {secondCrumb && getBreadcrumb(secondCrumb)}
        {shouldShowKebabMenu && (
          <li className={styles.intermediaryMenu}>
            <Menu
              component={
                <Button
                  ariaLabel={i18n.t('more', 'More')}
                  icon={<MoreHoriz sx={{ height: 18, width: 18 }} />}
                  size="auto"
                  variant="ghost"
                />
              }
              horizontalPosition="left"
              isBreadcrumbs
              shouldCloseOnTab={false}
              shouldSetFocus={false}
              variant="unbounded"
            >
              {freshBreadcrumbs.map((breadcrumb) => {
                const truncatedText = truncate(breadcrumb.text, {
                  length: maxChars,
                });
                return (
                  <MenuItem key={breadcrumb.key} className={styles.menuItem}>
                    <a href={breadcrumb.path}>
                      {breadcrumb.text?.length > maxChars ? (
                        <Tooltip content={breadcrumb.text}>{getMenuText(truncatedText)}</Tooltip>
                      ) : (
                        getMenuText(truncatedText)
                      )}
                    </a>
                  </MenuItem>
                );
              })}
            </Menu>
            <div className={styles.divider} aria-hidden="true">
              <KeyboardArrowRight sx={{ height: 10, width: 10 }} />
            </div>
          </li>
        )}
        {penultimateCrumb && getBreadcrumb(penultimateCrumb)}
        {lastCrumb && getBreadcrumb(lastCrumb)}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, path: PropTypes.string, text: PropTypes.string }),
  ),
  className: PropTypes.string,
  shouldInvertBreadcrumbs: PropTypes.bool,
  shouldShowAllAsActive: PropTypes.bool,
};

export { Breadcrumbs as BreadcrumbsStorybook };
export default React.memo(Breadcrumbs);
